/* eslint-disable i18next/no-literal-string */
import {FormHelperText, FormLabel, FormRow} from '@dropbox/dig-components/form_row';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Text} from '@dropbox/dig-components/typography';
import {Split, Stack} from '@dropbox/dig-foundations';

import {BannerDraft, DraftErrors, LIMITS} from './bannerForm';

type CopyField = 'title' | 'subtitle' | 'scrolling_strip' | 'cta_label' | 'cta_url';

const TextField = ({
  id,
  label,
  help,
  placeholder,
  value,
  maxLength,
  error,
  isRequired,
  onChange,
}: {
  id: string;
  label: string;
  help?: string;
  placeholder?: string;
  value: string;
  maxLength: number;
  error?: string;
  isRequired?: boolean;
  onChange: (value: string) => void;
}) => (
  <FormRow>
    <FormLabel htmlFor={id}>{label}</FormLabel>
    <TextInput
      id={id}
      placeholder={placeholder}
      value={value}
      maxLength={maxLength}
      required={isRequired}
      isInvalid={Boolean(error)}
      aria-describedby={help || error ? `${id}-help` : undefined}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
    {error ? (
      <Text id={`${id}-help`} size="small" color="error">
        {error}
      </Text>
    ) : (
      help && <FormHelperText id={`${id}-help`}>{help}</FormHelperText>
    )}
  </FormRow>
);

export const BannerCopyFields = ({
  draft,
  errors,
  onChange,
}: {
  draft: BannerDraft;
  errors: DraftErrors;
  onChange: (field: CopyField, value: string) => void;
}) => (
  <Stack gap="8">
    <TextField
      id="banner-title"
      label="Title"
      isRequired
      value={draft.title}
      maxLength={LIMITS.title}
      error={errors.title}
      onChange={(value) => onChange('title', value)}
    />
    <TextField
      id="banner-subtitle"
      label="Subtitle"
      placeholder="Optional"
      value={draft.subtitle}
      maxLength={LIMITS.copy}
      error={errors.subtitle}
      onChange={(value) => onChange('subtitle', value)}
    />
    <TextField
      id="banner-scrolling-strip"
      label="Scrolling strip"
      placeholder="Optional"
      help="Repeats across the top of the banner. Leave blank to hide the strip."
      value={draft.scrolling_strip}
      maxLength={LIMITS.copy}
      error={errors.scrolling_strip}
      onChange={(value) => onChange('scrolling_strip', value)}
    />
    <Split gap="16">
      <Split.Item width="fill">
        <TextField
          id="banner-cta-label"
          label="Button label"
          placeholder="Optional, e.g. Learn more"
          value={draft.cta_label}
          maxLength={LIMITS.ctaLabel}
          error={errors.cta}
          onChange={(value) => onChange('cta_label', value)}
        />
      </Split.Item>
      <Split.Item width="fill">
        <TextField
          id="banner-cta-url"
          label="Button link"
          placeholder="https://…"
          value={draft.cta_url}
          maxLength={LIMITS.ctaUrl}
          onChange={(value) => onChange('cta_url', value)}
        />
      </Split.Item>
    </Split>
  </Stack>
);
